import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Check, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { usePayloadList } from "@/hooks/usePayloads";
import type { PayloadSummary } from "@/types/api";

interface PayloadSelectorProps {
  selected: PayloadSummary[];
  onSelectionChange: (selected: PayloadSummary[]) => void;
}

export function PayloadSelector({ selected, onSelectionChange }: PayloadSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: payloads, isLoading, error, refetch } = usePayloadList();

  const selectedIds = useMemo(
    () => new Set(selected.map((p) => `${p.kind}:${p.identifier}`)),
    [selected]
  );

  const grouped = useMemo(() => {
    if (!payloads) return {};
    const filtered = payloads.filter((p) => {
      const q = searchQuery.toLowerCase();
      return (
        (p.title || "").toLowerCase().includes(q) ||
        (p.identifier || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q) ||
        (p.kind || "").toLowerCase().includes(q)
      );
    });
    const groups: Record<string, PayloadSummary[]> = {};
    filtered.forEach((p) => {
      const kind = p.kind || "Other";
      if (!groups[kind]) groups[kind] = [];
      groups[kind].push(p);
    });
    return groups;
  }, [payloads, searchQuery]);

  const togglePayload = (payload: PayloadSummary) => {
    const key = `${payload.kind}:${payload.identifier}`;
    if (selectedIds.has(key)) {
      onSelectionChange(selected.filter((p) => `${p.kind}:${p.identifier}` !== key));
    } else {
      onSelectionChange([...selected, payload]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading available payloads…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-destructive">Failed to load payloads</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          {error instanceof Error ? error.message : "An unexpected error occurred"}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  const sortedKinds = Object.keys(grouped).sort();

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search payloads by name, identifier, or kind…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-xs text-muted-foreground self-center mr-1">Selected:</span>
          {selected.map((p) => (
            <Badge
              key={`${p.kind}:${p.identifier}`}
              variant="default"
              className="gap-1 cursor-pointer"
              onClick={() => togglePayload(p)}
            >
              {p.title || p.identifier}
              <span className="text-xs opacity-70">×</span>
            </Badge>
          ))}
        </div>
      )}

      <div className="space-y-6 max-h-[400px] overflow-y-auto pr-1">
        {sortedKinds.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            {searchQuery ? "No payloads match your search." : "No payloads available."}
          </p>
        ) : (
          sortedKinds.map((kind) => (
            <div key={kind}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                {kind} <span className="font-normal">({grouped[kind].length})</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {grouped[kind].map((payload) => {
                  const key = `${payload.kind}:${payload.identifier}`;
                  const isSelected = selectedIds.has(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => togglePayload(payload)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                          : "border-border hover:border-primary/40 hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{payload.title || payload.identifier}</p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{payload.identifier}</p>
                          {payload.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{payload.description}</p>
                          )}
                        </div>
                        {isSelected && <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
